import { useTranslation } from 'react-i18next';
import { useCheckResponse } from '@src/hooks';
import { Button, message } from 'antd';
import { useChangePasswordMutation } from '@src/services/profile/profileApiSlice';
import { PageWrapper } from '@src/components';
import { FormPassword } from '@src/components/form';
import { FieldValues, useForm } from 'react-hook-form';

interface ChangePasswordProps {
  onClose: () => void;
}

export function ChangePassword({ onClose }: Readonly<ChangePasswordProps>) {
  const { t } = useTranslation();
  const { checkResponse } = useCheckResponse();
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
  } = useForm({ mode: 'onChange' });

  const onSubmit = async (formData: FieldValues) => {
    try {
      await changePassword({
        oldPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      }).unwrap();

      message.success(t('notification.success.changePassword'));
      onClose?.();
    } catch (error: any) {
      checkResponse(error);
    }
  };

  const handleDiscard = () => {
    reset();
    onClose?.();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <PageWrapper rootClassName='bg-wl-background text-wl-on-background py-6 px-4'>
        <p className='text-M-16 mb-6'>
          {t('pages.changePassword.description')}
        </p>
        <div className='grid sm:grid-flow-row gap-6'>
          <FormPassword
            t={t}
            control={control}
            name='currentPassword'
            placeholder={t('forms.currentPassword')}
            error={errors.currentPassword}
            className='border-none'
          />
          <FormPassword
            t={t}
            control={control}
            name='newPassword'
            placeholder={t('forms.newPassword')}
            error={errors.newPassword}
            showRules
            className='border-none'
          />
          <FormPassword
            t={t}
            control={control}
            name='confirmNewPassword'
            placeholder={t('forms.confirmNewPassword')}
            error={errors.confirmNewPassword}
            watch={watch}
            confirm
            referenceField='newPassword'
            className='border-none'
          />
        </div>
      </PageWrapper>
      <div className='flex justify-between gap-4 mt-8'>
        <Button className='btn-secondary w-1/2' onClick={handleDiscard}>
          {t('buttons.discard')}
        </Button>
        <Button
          htmlType='submit'
          className='btn-primary w-1/2'
          loading={isLoading}
        >
          {t('buttons.save')}
        </Button>
      </div>
    </form>
  );
}
